import styles from './style.postcss';

import React from 'react';
import { withRouter } from 'react-router';
import { bindActionCreators } from 'redux';
import { push } from 'react-router-redux';
import is from 'is_js';
import { List } from 'immutable';
import ReactGA from 'react-ga4';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
  moment,
  connect,
  ApiCall,
  ApiResponseHelper,
  Grid,
  PageCard,
  Icon,
  PrivilegeChecker,
  ConfirmDialog,
  Tooltip,
} from 'omni-common-ui';

import InternalNoteView from 'components/InternalNoteView';
import AddInternalNoteControl from 'components/AddInternalNoteControl';
import studentReportCommentActions from 'data/ProgressReports/StudentReportComment';
import { actions as homeworkActions } from 'data/OnlineHomework';
import {
  StudentReportSummary,
  actions as studentReportSummariesActions,
} from 'data/ProgressReports/StudentReportSummaries';
import { getCombinedUnitSummariesText } from 'data/ProgressReports/accessors';
import { actions as paperBasedHomeworkActions } from 'data/PaperBasedHomework';
import { getUnitList } from 'data/Units/accessors';
import { actions as Group } from 'containers/Group';
import { actions as GroupMembers } from 'data/GroupMembers';
import { actions as brandScoreActions } from 'data/ScoreBand';
import UnsavedChangesTrackingPageComponent from 'components/UnsavedChangesTrackingPageComponent';
import UnsavedChangesWarning from 'components/UnsavedChangesWarning';
import RejectNote from 'components/RejectNote';
import Privileges from 'domain/Privileges';
import { ReportStudentCard } from './ReportStudentCard';
import { ProgressTestCard } from './ProgressTestCard';
import OverallDevelopmentCard from './OverallDevelopmentCard';
import { HomeworkCard } from './HomeworkCard';
import { SkillScoreCard } from './SkillScoreCard';
import UserCard from 'components/UserCard';
import ExportDialog from './ExportDialog';
import { Modes } from './routes';

export class StudentReportView extends UnsavedChangesTrackingPageComponent {
  constructor(props) {
    super(props);
    this.state = { wasCommentEditMade: false };
  }

  componentDidMount() {
    this.props.fetchOnlineHomeworkIfNeeded(this.props.groupId);
    this.props.fetchGroupPaperBasedHomeworkIfNeeded(this.props.groupId);
    this.props.fetchScoreBandIfNeeded(this.props.language);
  }

  componentWillReceiveProps() {
    this.setState({ wasCommentEditMade: false });
  }

  routerWillLeave(nextLocation) {
    const {
      progressReportId,
      studentReportSummary,
      studentId,
      submitStudentReportSummaryTeacherPublish,
      groupId,
    } = this.props;
    if (nextLocation && nextLocation.pathname.endsWith('/publish')) {
      const studentReportSummaryIds = new List([studentReportSummary.id]);
      submitStudentReportSummaryTeacherPublish(
        progressReportId,
        groupId,
        studentReportSummaryIds,
        studentId,
      );
      // we want the changes dialog to show for this new route, so we won't use _forcePushRoute
      this.props.pushRoute(this.props.buildRoute({ mode: '' }));
    } else {
      return super.routerWillLeave(nextLocation);
    }
  }

  _isChangesPresent() {
    const { studentReportComment } = this.props;
    if (StudentReportView.isInAddCommentMode(this.props)) {
      return this.state.comment && this.state.comment.length;
    } else if (StudentReportView.isInEditCommentMode(this.props)) {
      return (
        !!this.state.wasCommentEditMade ||
        studentReportComment !== this.state.comment
      );
    }
    return false;
  }

  _goToViewModeAfterSaveOrDiscard() {
    this._forcePushRoute(this.props.buildRoute({ mode: '' }), {
      comment: undefined,
    });
  }

  _onDiscardCommentClick() {
    ReactGA.event({
      category: 'Progress Reports',
      action: 'Clicked "Discard"',
    });

    this._goToViewModeAfterSaveOrDiscard();
  }

  _onSaveComment(text) {
    ReactGA.event({
      category: 'Progress Reports',
      action: 'Clicked "Save"',
    });

    this._goToViewModeAfterSaveOrDiscard();
    const {
      progressReportId,
      studentReportId,
      submitStudentReportComment,
      fetchStudentReportSummary,
      studentId,
    } = this.props;
    submitStudentReportComment(
      progressReportId,
      studentReportId,
      text,
      studentId,
    ).then(() => fetchStudentReportSummary(progressReportId, studentId));
  }

  _onCommentChanged(text) {
    this.setState({
      comment: text,
      wasCommentEditMade: true,
    });
  }

  _isCommentEmpty() {
    return !this.state.comment || is.empty(this.state.comment.trim());
  }

  _onDeleteComment() {
    ReactGA.event({
      category: 'Progress Reports',
      action: 'Clicked "Delete"',
    });

    this._openDeleteCommentConfirmation();
  }

  _onDeleteCommentCancelled() {
    ReactGA.event({
      category: 'Progress Reports',
      action: 'Clicked "Cancel"',
    });

    this._closeDeleteCommentConfirmation();
  }

  _onDeleteCommentConfirmed() {
    ReactGA.event({
      category: 'Progress Reports',
      action: 'Clicked "Delete"',
    });

    this._closeDeleteCommentConfirmation();
    const {
      progressReportId,
      studentReportId,
      deleteStudentReportComment,
      fetchStudentReportSummary,
      studentId,
    } = this.props;
    deleteStudentReportComment(
      progressReportId,
      studentReportId,
      studentId,
    ).then(() => fetchStudentReportSummary(progressReportId, studentId));
  }

  _openDeleteCommentConfirmation() {
    this.setState({ showDeleteConfirm: true }, () => {
      ReactGA.send({
        hitType: 'pageview',
        page: window.location.pathname,
        location: window.location.href,
        title: document.title,
        modal: 'delete-report-comment-confirmation',
      });
    });
  }

  _closeDeleteCommentConfirmation() {
    this.setState({ showDeleteConfirm: false }, () => {
      ReactGA.send({
        hitType: 'pageview',
        page: window.location.pathname,
        location: window.location.href,
        title: document.title,
      });
    });
  }

  _onEditComment(comment) {
    ReactGA.event({
      category: 'Progress Reports',
      action: 'Clicked "Edit"',
    });

    this.setState({ comment });
    this.props.pushRoute(this.props.buildRoute({ mode: 'edit-comment' }));
  }

  _isCommentEditable(reportStatus) {
    return (
      this.props.canAddComment &&
      ((!is.undefined(reportStatus) &&
        reportStatus !== StudentReportSummary.Status.SentToDoS &&
        reportStatus !== StudentReportSummary.Status.Completed &&
        reportStatus !== StudentReportSummary.Status.Published) ||
        this.props.canUpdatePublishedProgressReport)
    );
  }

  _renderExportDialog() {
    const {
      buildRoute,
      pushRoute,
      languageCode,
      progressReportId,
      studentReportSummary,
      studentId,
    } = this.props;
    const isInExportRoute = this.props.params.mode === Modes.EXPORT;
    return (
      isInExportRoute && (
        <ExportDialog
          progressReportId={progressReportId}
          languageCode={languageCode}
          pushRoute={pushRoute}
          buildRoute={buildRoute}
          studentReportSummary={studentReportSummary}
          studentId={studentId}
        />
      )
    );
  }
  _renderCommentsViewMode(comment, reportStatus) {
    const { studentReportSummary, timeZone } = this.props;
    const commentViewMode = {
      note: comment,
      userId: studentReportSummary && studentReportSummary.commentedByUserId,
      date: studentReportSummary && studentReportSummary.commentedTimeUtc,
      timeZone,
    };
    return (
      <InternalNoteView
        note={commentViewMode}
        canOperate={this._isCommentEditable(reportStatus)}
        hideActionButtons
      />
    );
  }

  _renderCommentsEditor() {
    return (
      <AddInternalNoteControl
        text={this.state.comment}
        maxCharacters={500}
        onChange={(text) => this._onCommentChanged(text)}
        onDiscardClick={() => this._onDiscardCommentClick()}
        onSaveClick={(text) => this._onSaveComment(text)}
      />
    );
  }

  _renderActionIcons(reportStatus, studentReportComment) {
    if (
      !this._isCommentEditable(reportStatus) ||
      StudentReportView.isInAddCommentMode(this.props) ||
      StudentReportView.isInEditCommentMode(this.props)
    ) {
      return null;
    }

    return [
      <Tooltip
        className={styles.StudentReportView_card_icon}
        text="Edit"
        placement="top"
        trigger={['hover']}
      >
        <Icon
          id="edit"
          onClick={() => this._onEditComment(studentReportComment)}
        />
      </Tooltip>,
      <Tooltip
        className={styles.StudentReportView_card_icon}
        text="Delete"
        placement="top"
        trigger={['hover']}
      >
        <Icon
          id="trash"
          title="Delete"
          onClick={() => this._onDeleteComment()}
        />
      </Tooltip>,
    ];
  }

  _renderStudentReportComment(comment, reportStatus) {
    if (
      StudentReportView.isInAddCommentMode(this.props) ||
      StudentReportView.isInEditCommentMode(this.props)
    ) {
      return this._renderCommentsEditor(comment);
    }
    return this._renderCommentsViewMode(comment, reportStatus);
  }

  _renderUnitSummary() {
    const { progressReportUnitSummary } = this.props;
    if (! progressReportUnitSummary) {
      return null;
    }

    return (
      <Grid.Item xs={12} sm={12} md={12} lg={12}>
        <PageCard className={classnames(styles.StudentReportView_card)}>
          <PageCard.Heading text="Unit Summary" />
          <PageCard.Content className={styles.StudentReportView_unitSummary_body}>
            {progressReportUnitSummary}
          </PageCard.Content>
        </PageCard>
      </Grid.Item>
    );
  }

  _renderTeacherCommentsGridItem(studentReportComment, reportStatus) {
    if (StudentReportView.isInAddCommentMode(this.props)) {
      return (
        <Grid.Item xs={12} sm={12} md={12} lg={12}>
          <PageCard className={classnames(styles.StudentReportView_card)}>
            <PageCard.Heading text="Teacher's Comment" />
            <PageCard.Content>
              {this._renderStudentReportComment()}
            </PageCard.Content>
          </PageCard>
        </Grid.Item>
      );
    }

    if (studentReportComment && studentReportComment.length > 0) {
      return (
        <Grid.Item xs={12} sm={12} md={12} lg={12}>
          <PageCard className={classnames(styles.StudentReportView_card)}>
            <PageCard.Heading text="Teacher's Comment" stackMode="horizontal">
              {this._renderActionIcons(reportStatus, studentReportComment)}
            </PageCard.Heading>
            <PageCard.Content>
              {this._renderStudentReportComment(
                studentReportComment,
                reportStatus,
              )}
            </PageCard.Content>
          </PageCard>
        </Grid.Item>
      );
    }

    return null;
  }

  _renderProgressTestCardGridItem(
    percentage,
    publishedDateStr,
    name,
    isSSCourseTypeGroup,
    isSSV3Group,
  ) {
    if (is.not.number(percentage) || isSSV3Group) {
      return null;
    }

    return (
      <Grid.Item xs={12} sm={12} md={6} lg={6}>
        <ProgressTestCard
          className={styles.StudentReportView_card}
          percentage={percentage}
          publishedDateStr={publishedDateStr}
          name={name}
          isSSCourseTypeGroup={isSSCourseTypeGroup}
        />
      </Grid.Item>
    );
  }

  _shouldShowPaperBasedInHomeworkCard() {
    return !!this.props.hasPaperBasedHomework;
  }

  _renderHomeworkCardGridItem() {
    const { onlineHomeworkResult, paperBasedHomeworkResult } = this.props;
    if (is.undefined(this.props.onlineHomeworkResult)) {
      return null;
    }

    return (
      <Grid.Item xs={12} sm={12} md={6} lg={6}>
        <HomeworkCard
          className={styles.StudentReportView_card}
          averageScorePercentage={onlineHomeworkResult.averageScorePercentage}
          completedAssignments={onlineHomeworkResult.completedAssignments}
          totalAssignments={onlineHomeworkResult.totalAssignments}
          shouldShowPaperBased={this._shouldShowPaperBasedInHomeworkCard()}
          completedPaperBased={paperBasedHomeworkResult.completedAssignments}
          totalPaperBased={paperBasedHomeworkResult.totalAssignments}
        />
      </Grid.Item>
    );
  }

  _renderOverallDevelopmentCard(
    studentProgressTestResult,
    progressTestSkills,
    isSSV3CourseTypeGroup,
  ) {
    if (!isSSV3CourseTypeGroup) {
      return null;
    }

    const rubricSkills = progressTestSkills.filter((s) => s.isRubric);
    const rubricSkillResults = studentProgressTestResult.skillResults.filter(
      (sr) => sr.isRubric,
    );
    return (
      <Grid.Item xs={12} sm={12} md={12} lg={12}>
        <OverallDevelopmentCard
          className={styles.StudentReportView_card}
          skills={rubricSkills}
          skillResults={rubricSkillResults}
        />
      </Grid.Item>
    );
  }

  _renderSkillScoreCardGridItems(
    studentProgressTestResult,
    progressTestSkills,
    isSSCourseTypeGroup,
    isSSV3Group,
  ) {
    const getStudentResultForSkill = (skillId) =>
      studentProgressTestResult.skillResults.find(
        (sr) => sr.skillId === skillId,
      );

    const items = progressTestSkills
      .filter((skill) => {
        const studentResultForSkill = getStudentResultForSkill(skill.skillId);
        if (isSSV3Group && studentResultForSkill.isRubric) {
          return false;
        }
        return is.number(studentResultForSkill.currentResult);
      })
      .map((skill) => {
        const studentResultForSkill = getStudentResultForSkill(skill.skillId);
        const percentage = Number.parseInt(
          (studentResultForSkill.currentResult /
            studentResultForSkill.fullMark) *
            100,
          10,
        );
        return (
          <Grid.Item key={skill.skillId} xs={12} sm={12} md={6} lg={6}>
            <SkillScoreCard
              skillName={skill.name}
              skillDescription={skill.description}
              scorePercentage={percentage}
              isSSCourseTypeGroup={isSSCourseTypeGroup}
              studentResultForSkill={studentResultForSkill}
            />
          </Grid.Item>
        );
      });
    return items;
  }

  _renderDeleteCommentDialog() {
    return (
      <ConfirmDialog
        isOpen={this.state.showDeleteConfirm}
        content="Are you sure you want to delete this comment?"
        primaryButtonContent="CANCEL"
        secondaryButtonContent="DELETE"
        onPrimaryClick={() => this._onDeleteCommentCancelled()}
        onSecondaryClick={() => this._onDeleteCommentConfirmed()}
      />
    );
  }

  _renderUnsavedChangesWarningDialog() {
    if (
      StudentReportView.isInEditCommentMode(this.props) &&
      this._isCommentEmpty()
    ) {
      return (
        <ConfirmDialog
          isOpen={!!this._isNavigationWithChangesRequested()}
          primaryButtonContent="Stay"
          secondaryButtonContent="Leave"
          content="Are you sure you want to leave this page and lose unsaved changes?"
          onPrimaryClick={() => {
            this._clearRequestedLocation();
          }}
          onSecondaryClick={() => {
            this._forcePushRequestedLocation();
          }}
          onRequestClose={() => {
            this._clearRequestedLocation();
          }}
        />
      );
    }
    return (
      <UnsavedChangesWarning
        isOpen={!!this._isNavigationWithChangesRequested()}
        isSaving={this.props.isAnyApiCallSaving}
        onSaveClicked={() => {
          const { comment: text } = this.state;
          this._onSaveComment(text);
        }}
        onDiscardClicked={() => {
          this._forcePushRequestedLocation();
        }}
        onCloseClicked={() => {
          this._clearRequestedLocation();
        }}
        onSaved={() => {
          this._forcePushRequestedLocation();
        }}
      />
    );
  }

  _renderRjectNote() {
    const { studentReportSummary = {}, timeZone } = this.props;
    const reportStatus = studentReportSummary.status || '';
    const dosRejectedComment =
      studentReportSummary && studentReportSummary.dosRejectedComment;

    return (
      dosRejectedComment &&
      reportStatus.toLowerCase() ===
        StudentReportSummary.Status.Rejected.toLowerCase() && (
        <RejectNote
          note={dosRejectedComment.rejectedComment}
          rejectTime={dosRejectedComment.rejectedDate}
          timeZone={timeZone}
          teacherId={dosRejectedComment.rejectedBy}
        />
      )
    );
  }

  render() {
    const {
      group,
      member,
      progressTest,
      progressTestUnitName,
      studentProgressTestResult,
      studentReportComment,
      studentReportSummary,
      isSSCourseTypeGroup,
      isSSV3Group,
      timeZone,
    } = this.props;

    const { skills: progressTestSkills } = progressTest.data;

    if (is.undefined(member)) {
      return null;
    }
    const { studentGeneralInfo } = member;
    const studentName = `${studentGeneralInfo.firstName} ${studentGeneralInfo.lastName}`;
    const teachers = group.get('teachers');

    let reportStatus;
    let reportPublishedDateStr = null;
    if (!is.undefined(studentReportSummary)) {
      reportStatus = studentReportSummary.status;
      reportPublishedDateStr = studentReportSummary.publishedTimeUtc
        ? moment
            .utc(studentReportSummary.publishedTimeUtc)
            .clone()
            .tz(timeZone)
            .format('YYYY-MM-DD')
        : null;
    }

    return (
      <div>
        {this._renderExportDialog()}
        {this._renderUnsavedChangesWarningDialog()}
        {this._renderDeleteCommentDialog()}
        <Grid>
          <Grid.Group>
            <Grid.Item xs={12} sm={12} md={12} lg={12}>
              {this._renderRjectNote()}
            </Grid.Item>
            {this._renderUnitSummary()}
            <Grid.Item xs={12} sm={12} md={6} lg={6}>
              <ReportStudentCard
                studentName={studentName}
                studentGeneralInfo={studentGeneralInfo}
                member={member}
                reportStatus={is.undefined(reportStatus) ? '-' : reportStatus}
                submittedDate={reportPublishedDateStr}
              />
            </Grid.Item>
            <Grid.Item xs={12} sm={12} md={6} lg={6}>
              <PageCard
                className={classnames(
                  styles.StudentReportView_card,
                  styles.StudentReportView_teachers_card,
                )}
              >
                <PageCard.Heading text="Teachers" />
                <PageCard.Content>
                  <div className={styles.StudentReportView_teachers_list}>
                    {teachers.map((teacher) => (
                      <UserCard
                        id={`${teacher.get('userId')}`}
                        key={`user-${teacher.get('userId')}`}
                        className={styles.StudentReportView_teachers_item}
                      />
                    ))}
                  </div>
                </PageCard.Content>
              </PageCard>
            </Grid.Item>
            {this._renderProgressTestCardGridItem(
              studentProgressTestResult.totalScore.percentage,
              reportPublishedDateStr,
              progressTestUnitName,
              isSSCourseTypeGroup,
              isSSV3Group,
            )}
            {this._renderHomeworkCardGridItem()}
            {this._renderTeacherCommentsGridItem(
              studentReportComment,
              reportStatus,
            )}
          </Grid.Group>
          <Grid.Group>
            {this._renderOverallDevelopmentCard(
              studentProgressTestResult,
              progressTestSkills,
              isSSV3Group,
            )}
            {this._renderSkillScoreCardGridItems(
              studentProgressTestResult,
              progressTestSkills,
              isSSCourseTypeGroup,
              isSSV3Group,
            )}
          </Grid.Group>
        </Grid>
      </div>
    );
  }
}

StudentReportView.isInAddCommentMode = (props) =>
  props.params && props.params.mode === 'add-comment';

StudentReportView.isInEditCommentMode = (props) =>
  props.params && props.params.mode === 'edit-comment';

StudentReportView.propTypes = {
  group: PropTypes.object,
  member: PropTypes.object.isRequired,
  timeZone: PropTypes.string.isRequired,
  hasPaperBasedHomework: PropTypes.bool,
  progressReportId: PropTypes.string,
  progressTestUnitName: PropTypes.string,
  progressTest: PropTypes.object,
  studentProgressTestResult: PropTypes.object,
  studentReportSummary: PropTypes.object,
  studentId: PropTypes.string,
  studentReportId: PropTypes.string,
  groupId: PropTypes.string,
  studentReportComment: PropTypes.object,
  progressReportUnitSummary: PropTypes.string,
  pushRoute: PropTypes.func,
  deleteStudentReportComment: PropTypes.func,
  fetchStudentReportComment: PropTypes.func,
  fetchStudentReportSummary: PropTypes.func,
  fetchOnlineHomeworkIfNeeded: PropTypes.func,
  fetchGroupPaperBasedHomeworkIfNeeded: PropTypes.func,
  submitStudentReportComment: PropTypes.func,
  submitStudentReportSummaryTeacherPublish: PropTypes.func,
  onlineHomeworkResult: PropTypes.object,
  paperBasedHomeworkResult: PropTypes.object,
  buildRoute: PropTypes.func.isRequired,
  router: PropTypes.any.isRequired,
  routes: PropTypes.array.isRequired,
  route: PropTypes.any.isRequired,
  location: PropTypes.any.isRequired,
  canAddComment: PropTypes.bool,
  canUpdatePublishedProgressReport: PropTypes.bool,
  isAnyApiCallSaving: PropTypes.bool,
  isSSCourseTypeGroup: PropTypes.bool,
  isSSV3Group: PropTypes.bool,
  language: PropTypes.string,
  languageCode: PropTypes.string,
};

export function mapStateToProps(state, ownProps) {
  const { groupId, studentId, progressReportId } = ownProps.params;
  const { timeZone } = Group.getCentre(state, groupId);
  const hasPaperBasedHomework = Group.hasPaperBasedHomework(state, groupId);
  const canAddComment = PrivilegeChecker.hasPrivilege(
    state,
    Privileges.ProgressReport.AddComment,
  );
  const canUpdatePublishedProgressReport = PrivilegeChecker.hasPrivilege(
    state,
    Privileges.ProgressReport.UpdatePublishedProgressReport,
  );
  const group = state.get('rootReducer').get('groups').get(groupId).data;
  const member = GroupMembers.getMemberById(state, studentId);
  const studentReportSummaries = state
    .get('rootReducer')
    .get('studentReportSummaries')
    .get(`${progressReportId}`).data;
  const progressTestsPlan = state
    .get('rootReducer')
    .get('progressTestsPlan')
    .get(groupId).data;
  const studentReportSummary =
    studentReportSummaries && studentReportSummaries.get(`${studentId}`);
  const studentReportComment =
    studentReportSummary && studentReportSummary.comments;
  const progressReportRecord = group.progressReports.data.get(progressReportId);
  const progressReportUnitSummary = getCombinedUnitSummariesText(progressReportRecord);
  const { progressTestId } = progressReportRecord;
  const progressTest = state
    .get('rootReducer')
    .get('progressTests')
    .get(progressTestId);
  let studentProgressTestResult;
  if (ApiResponseHelper.hasSucceeded(progressTest)) {
    studentProgressTestResult =
      progressTest.data.studentProgressTestResults.get(studentId);
  }

  const currentUnits = progressTestsPlan.find(
    (plan) => plan.progressReportId === progressReportId,
  ).unitIds;
  const units = getUnitList(state, groupId) || [];
  const progressTestUnit = units
    .filter((unit) => currentUnits.includes(unit.id))
    .toList()
    .get(0);
  const onlineHomeworkResult =
    studentReportSummary && studentReportSummary.onlineHomeworkResult;
  const paperBasedHomeworkResult =
    studentReportSummary && studentReportSummary.paperBasedHomeworkResult;

  const language = state.get('rootReducer').get('language');
  const languageCode = group.languageCode;
  return {
    group,
    timeZone,
    hasPaperBasedHomework,
    member,
    progressReportId,
    progressTestUnitName: progressTestUnit && progressTestUnit.name,
    progressTest,
    studentProgressTestResult,
    studentReportSummary,
    studentReportId: studentReportSummary && studentReportSummary.id,
    studentId,
    groupId,
    studentReportComment,
    progressReportUnitSummary,
    canAddComment,
    canUpdatePublishedProgressReport,
    onlineHomeworkResult,
    paperBasedHomeworkResult,
    isAnyApiCallSaving: !!isAnyApiCallSaving(state),
    isSSCourseTypeGroup: !!Group.isSSCourseTypeGroup(state, groupId),
    isSSV3Group: !!Group.isSSV3Group(state, groupId),
    language,
    languageCode,
  };
}

function mapDispatchToProps(dispatch) {
  return Object.assign(
    { pushRoute: (path) => dispatch(push(path)) },
    bindActionCreators(studentReportSummariesActions, dispatch),
    bindActionCreators(studentReportCommentActions, dispatch),
    bindActionCreators(homeworkActions, dispatch),
    bindActionCreators(brandScoreActions, dispatch),
    bindActionCreators(paperBasedHomeworkActions, dispatch),
  );
}

function isAnyApiCallSaving(state) {
  return !!(state.get('apiCalls') || new List())
    .filter((call, key) =>
      ['PUT', 'POST'].some((method) => key.startsWith(method)),
    )
    .find((call) => ApiCall.State.isLoading(call));
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(StudentReportView));



// WEBPACK FOOTER //
// ./containers/Group/ProgressReport/StudentReportView/component.jsx